// Generic, simple header renderer for Acro JSON
// - Considers ONLY the first object with type: 'header'
// - Renders 'title' nodes as plain text
// - Skips 'group' containers and renders their children fields directly
// - Maps 'edittext' to <input type="text"> and 'checkbox' to <input type="checkbox">
// Keep this file minimal and easy to tweak

export type AcroNode = {
  id?: string | null;
  type?: string | null;
  title?: string | null;
  field_id?: string | null;
  sub_title?: string | null;
  description?: string | null;
  placeholder?: string | null;
  readonly?: boolean | null;
  multiline?: boolean | null;
  options?: unknown[] | null;
  checked?: boolean | null;
  dateValue?: string | null;
  validation?: unknown | null;
  table_value?: unknown | null;
  children?: AcroNode[] | null;
};

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function renderEditText(node: AcroNode): string {
  const id = escapeHtml(String(node.id ?? ""));
  const fieldId = node.field_id ? escapeHtml(String(node.field_id)) : "";
  const title = node.title ? escapeHtml(String(node.title)) : "";
  const placeholder = escapeHtml(String(node.placeholder ?? ""));
  const multiline = !!node.multiline;
  const label = title ? `<label class="text-sm text-gray-700">${title}</label>` : "";
  const fieldIdAttr = fieldId ? ` data-field-id="${fieldId}"` : "";
  const input = multiline
    ? `<textarea data-id="${id}"${fieldIdAttr} class="form-textarea w-full" placeholder="${placeholder}"></textarea>`
    : `<input type="text" data-id="${id}"${fieldIdAttr} class="form-input w-full" placeholder="${placeholder}" />`;
  return `<div class="header-field-wrapper">${label}${input}</div>`;
}

function renderCheckbox(node: AcroNode): string {
  const id = escapeHtml(String(node.id ?? ""));
  const fieldId = node.field_id ? escapeHtml(String(node.field_id)) : "";
  const title = node.title ? escapeHtml(String(node.title)) : "";
  const fieldIdAttr = fieldId ? ` data-field-id="${fieldId}"` : "";
  const input = `<input type="checkbox" data-id="${id}"${fieldIdAttr} class="form-checkbox" ${node.checked ? "checked" : ""} />`;
  const label = `<span class="text-sm">${title}</span>`;
  return `<label class="flex items-center gap-2">${input}${label}</label>`;
}

export function renderHeader(firstHeader?: AcroNode | null): string {
  if (!firstHeader) return "";
  const parts: string[] = [];

  // If both title and sub_title exist, render only those and skip children
  if (firstHeader.title && firstHeader.sub_title) {
    parts.push(`<div class="basis-full text-xl font-semibold text-gray-900">${escapeHtml(String(firstHeader.title))}</div>`);
    parts.push(`<div class="basis-full text-sm text-gray-600 whitespace-pre-line">${escapeHtml(String(firstHeader.sub_title))}</div>`);
    return `<div class="header flex flex-wrap gap-2">${parts.join("")}</div>`;
  }

  // Otherwise, render title/sub_title if present, then children

  const children = (firstHeader.children ?? []) as AcroNode[];
  children.forEach((c) => {
    const t = (c.type || "").toLowerCase();
    if (t === "title") {
      if (c.title) parts.push(`<div class="basis-full text-base text-gray-900">${escapeHtml(String(c.title))}</div>`);
    } else if (t === "group") {
      (c.children ?? []).forEach((gChild) => {
        const gt = (gChild.type || "").toLowerCase();
        if (gt === "title") {
          if (gChild.title) parts.push(`<div class="basis-full text-base text-gray-900">${escapeHtml(String(gChild.title))}</div>`);
        } else if (gt === "edittext") {
          parts.push(renderEditText(gChild));
        } else if (gt === "checkbox") {
          parts.push(renderCheckbox(gChild));
        }
      });
    } else if (t === "edittext") {
      parts.push(renderEditText(c));
    } else if (t === "checkbox") {
      parts.push(renderCheckbox(c));
    }
  });

  return parts.length > 0 ? `<div class="header flex flex-wrap gap-2">${parts.join("")}</div>` : "";
}
